import type {
  V2DetailOverlayItem,
  V2DetailOverlaySection,
} from '../../../components/v2-detail-overlay/v2-detail-overlay.component';

export type TradeBalanceType = 'Unbalanced' | 'Balanced' | 'Great';

export type TradePreviewSide = 'give' | 'receive';

export function getGiveItems(req: any): any[] {
  return (
    req?.ReqItemsDetailsAll?.filter(
      (item: any) => item.type === 'Give' || item.type === 'Give Full'
    ) ?? []
  );
}

export function getReceiveItems(req: any): any[] {
  return (
    req?.ReqItemsDetailsAll?.filter(
      (item: any) => item.type === 'Receive' || item.type === 'Receive Full'
    ) ?? []
  );
}

export function getGiveOtherColsItems(req: any): any[] {
  if (!req) {
    return [];
  }

  return (
    req.OtherCols?.flatMap((col: any) => {
      if (col.collection_id === req.collection_id) {
        return [];
      }
      return (col.ReqItemsDetailsAll ?? []).filter(
        (item: any) => item.type === 'Give' || item.type === 'Give Full'
      );
    }) ?? []
  );
}

export function getReceiveOtherColsItems(req: any): any[] {
  if (!req) {
    return [];
  }

  return (
    req.OtherCols?.flatMap((col: any) => {
      if (col.collection_id === req.collection_id) {
        return [];
      }
      return (col.ReqItemsDetailsAll ?? []).filter(
        (item: any) => item.type === 'Receive' || item.type === 'Receive Full'
      );
    }) ?? []
  );
}

export function getTradeBalanceType(req: any): TradeBalanceType {
  const gives = Number(req?.grand_total_gives ?? 0);
  const receives = Number(req?.grand_total_receives ?? 0);
  if (gives > receives) {
    return 'Unbalanced';
  }
  if (gives === receives) {
    return 'Balanced';
  }
  return 'Great';
}

function tradeItemStickerFile(item: any): string {
  if (!item) {
    return '';
  }
  const v =
    item.icon_path ??
    item.small_icon_path ??
    item.ICON_PATH ??
    item.SMALL_ICON_PATH;
  if (v == null || v === '') {
    return '';
  }
  return String(v).trim();
}

function tradeStickerPlaceholderFile(
  item: any,
  userCollectionDetails: any
): string {
  if (!userCollectionDetails) {
    return '';
  }
  const isL = String(item?.orientation ?? 'P').toUpperCase() === 'L';
  const raw = isL
    ? userCollectionDetails.no_sticker_img_l
    : userCollectionDetails.no_sticker_img_p;
  const file =
    raw == null || raw === ''
      ? userCollectionDetails.binder_cover
      : raw;
  if (file == null || file === '') {
    return '';
  }
  return String(file).trim();
}

function joinStickerBasePath(prefix: string, file: string): string {
  const p = String(prefix).replace(/\/+$/, '');
  const f = String(file).replace(/^\/+/, '');
  return `${p}/${f}`;
}

export function stickerCdnPrefix(userCollectionDetails: any): string {
  const fromDetails =
    userCollectionDetails?.icon_location_prefix ??
    userCollectionDetails?.icon_path_prefix;
  return fromDetails ? String(fromDetails).trim() : '';
}

export function getTradeStickerImgSrc(
  item: any,
  collectionType: string,
  userCollectionDetails: any,
  collectionIconPath?: string
): string {
  if (collectionType !== 'V') {
    return collectionIconPath || 'assets/images/logo-letter.svg';
  }

  const prefix = stickerCdnPrefix(userCollectionDetails);
  if (!prefix) {
    return 'assets/images/logo-letter.svg';
  }

  const file = tradeItemStickerFile(item);
  if (file) {
    return joinStickerBasePath(prefix, file);
  }

  const placeholder = tradeStickerPlaceholderFile(item, userCollectionDetails);
  if (placeholder) {
    return joinStickerBasePath(prefix, placeholder);
  }

  return 'assets/images/logo-letter.svg';
}

export function formatTradeRequestDate(date: string | Date): string {
  try {
    return new Date(date).toLocaleString();
  } catch {
    return '';
  }
}

export interface TradeRequestStatusOptions {
  /** History tab / read-only overlay — completed trades, not actionable pending. */
  history?: boolean;
}

function normalizeTradeRequestStatus(req: any): string {
  return String(req?.request_status ?? '').trim().toLowerCase();
}

export function getTradeRequestStatusKey(
  req: any,
  options?: TradeRequestStatusOptions
): string {
  const status = normalizeTradeRequestStatus(req);

  if (status === 'cancelled') {
    return 'cancelled';
  }
  if (status === 'closed') {
    return 'closed';
  }
  if (options?.history) {
    return 'closed';
  }
  if (status === 'accepted') {
    return 'TRADE_REQUESTS_SECTION_ACCEPTED';
  }
  if (status === 'sent') {
    if (req?.is_valid == 0) {
      return 'CANCEL_INVALID_REQUEST';
    }
    return 'PENDING';
  }
  if (status === 'created') {
    return 'CANCEL_INVALID_REQUEST';
  }
  return 'PENDING';
}

/** Matches trade-request-details footer decline button visibility. */
export function shouldShowTradeDeclineBtn(req: any, readOnly: boolean): boolean {
  if (readOnly || !req) {
    return false;
  }
  if (req.request_status === 'created') {
    return true;
  }
  if (req.request_status_type !== 'online') {
    return false;
  }
  if (req.is_valid == 0) {
    return req.request_status === 'sent';
  }
  if (req.is_valid == 1 && req.request_status === 'accepted') {
    return true;
  }
  if (req.is_valid == 1 && req.request_status === 'sent') {
    return req.type === 'Sent' || req.type === 'Received';
  }
  return false;
}

/** Matches trade-request-details footer accept button visibility. */
export function shouldShowTradeAcceptBtn(req: any, readOnly: boolean): boolean {
  if (readOnly || !req) {
    return false;
  }
  return (
    req.is_valid == 1 &&
    req.request_status_type === 'online' &&
    req.type === 'Received' &&
    req.request_status === 'sent'
  );
}

/** Matches trade-request-details footer close button visibility. */
export function shouldShowTradeCloseBtn(req: any, readOnly: boolean): boolean {
  if (readOnly || !req) {
    return false;
  }
  return (
    req.request_status_type === 'online' &&
    req.is_valid == 1 &&
    req.request_status === 'accepted'
  );
}

/**
 * Decline/cancel button label for the trade-requests inbox overlay.
 * Matches the pre-rebrand reel (`trade-requests.page`), not `trade-request-details`.
 * Virtual (V) and physical (P) collections used the same keys there; only accepted
 * trades used "Não Recebi" (`CANCEL_REQUEST`).
 */
export function getTradeRequestDeclineLabelKey(
  req: any,
  _listNav: '' | 'Received' | 'Sent' | 'History' = ''
): string {
  if (!req) {
    return 'CANCEL_REQUEST';
  }

  const status = String(req.request_status ?? '').trim().toLowerCase();
  const statusType = String(req.request_status_type ?? '').trim().toLowerCase();
  const isValid = req.is_valid == 1;
  const isOnline = statusType === 'online';

  if (status === 'created') {
    return 'CANCEL_INVALID_REQUEST';
  }

  if (isOnline && req.is_valid == 0 && status === 'sent') {
    return 'CANCEL_INVALID_REQUEST';
  }

  // Reel: accepted + decline → Não Recebi
  if (isValid && isOnline && status === 'accepted') {
    return 'CANCEL_REQUEST';
  }

  // Reel: Sent or Received + pending → Cancelar (same for V and P)
  if (isValid && isOnline && status === 'sent') {
    return 'TRADE_REQUEST_CANCEL';
  }

  return 'CANCEL_REQUEST';
}

export function formatTradeRequestRelativeDate(date: string | Date): string {
  try {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'now';
    }
    if (diffMins < 60) {
      return `${diffMins}m`;
    }
    if (diffHours < 24) {
      return `${diffHours}h`;
    }
    if (diffDays === 1) {
      return 'Yesterday';
    }
    if (diffDays < 7) {
      return `${diffDays}d`;
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return formatTradeRequestDate(date);
  }
}

export function getTradePreviewItems(
  req: any,
  side: TradePreviewSide,
  max: number
): any[] {
  const items = side === 'give' ? getGiveItems(req) : getReceiveItems(req);
  return items.slice(0, max);
}

export function getTradePreviewImgUrls(
  req: any,
  collectionType: string,
  userCollectionDetails: any,
  collectionIconPath?: string,
  giveMax = 2,
  receiveMax = 1
): { give: string[]; receive: string[] } {
  const giveItems = getTradePreviewItems(req, 'give', giveMax);
  const receiveItems = getTradePreviewItems(req, 'receive', receiveMax);
  return {
    give: giveItems.map((item) =>
      getTradeStickerImgSrc(
        item,
        collectionType,
        userCollectionDetails,
        collectionIconPath
      )
    ),
    receive: receiveItems.map((item) =>
      getTradeStickerImgSrc(
        item,
        collectionType,
        userCollectionDetails,
        collectionIconPath
      )
    ),
  };
}

export function mapTradeItemsToOverlayItems(items: any[]): V2DetailOverlayItem[] {
  return items.map((item) => ({
    item_id: item.item_id,
    item_name: item.item_name,
    icon_path: item.icon_path ?? item.small_icon_path,
    orientation: item.orientation,
    video: item.video,
    description_1: item.description_1,
    description_2: item.description_2,
    Tenho: item.Tenho,
    icon_color: item.icon_color,
    ...item,
  }));
}

export function buildTradeStickerOverlaySections(
  req: any,
  giveLabel: string,
  receiveLabel: string
): V2DetailOverlaySection[] {
  const giveItems = mapTradeItemsToOverlayItems(getGiveItems(req));
  const receiveItems = mapTradeItemsToOverlayItems(getReceiveItems(req));
  const sections: V2DetailOverlaySection[] = [];
  if (receiveItems.length) {
    sections.push({ section_name: receiveLabel, item: receiveItems });
  }
  if (giveItems.length) {
    sections.push({ section_name: giveLabel, item: giveItems });
  }
  return sections;
}

export function getTradeBalanceLabelKey(type: TradeBalanceType): string {
  if (type === 'Unbalanced') {
    return 'TRADE_REQUESTS_BALANCE_UNBALANCED';
  }
  if (type === 'Balanced') {
    return 'TRADE_REQUESTS_BALANCE_FAIR';
  }
  return 'TRADE_REQUESTS_BALANCE_GREAT';
}

export function isLargeTrade(req: any, threshold = 8): boolean {
  const gives = Number(req?.grand_total_gives ?? 0);
  const receives = Number(req?.grand_total_receives ?? 0);
  return gives + receives >= threshold;
}

export function getTradeRequestStatusClass(
  req: any,
  options?: TradeRequestStatusOptions
): string {
  const status = normalizeTradeRequestStatus(req);

  if (status === 'cancelled') {
    return 'cancelled';
  }
  if (status === 'closed' || options?.history) {
    return 'history';
  }
  if (status === 'accepted') {
    return 'accepted';
  }
  if (status === 'sent') {
    return 'pending';
  }
  return 'pending';
}

/** Footer label for completing an accepted online trade (matches old reel). */
export function getTradeRequestCloseLabelKey(): string {
  return 'BUTTON_CLOSE_REQUEST';
}

export function hasPreferredTradeLocations(
  req: any,
  collectionType: string
): boolean {
  if (!req || collectionType === 'V') {
    return false;
  }
  if (req.request_status_type !== 'online') {
    return false;
  }
  const loc = req.preferred_location_1;
  return loc != null && String(loc).trim() !== '';
}

export function getPreferredTradeLocations(req: any): string[] {
  if (!req) {
    return [];
  }
  return [req.preferred_location_1, req.preferred_location_2, req.preferred_location_3]
    .map((loc) => (loc == null ? '' : String(loc).trim()))
    .filter((loc) => loc !== '');
}

export function showMailTradeHelp(req: any, collectionType: string): boolean {
  return collectionType !== 'V' && !!req?.trade_by_mail;
}

export function showNonVTradeMetadata(
  req: any,
  collectionType: string
): boolean {
  return (
    hasPreferredTradeLocations(req, collectionType) ||
    showMailTradeHelp(req, collectionType)
  );
}

function escapeTradePopupHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildTradePreferencesPopupHtml(
  locations: string[],
  includeMail: boolean,
  mailLabel: string
): string {
  const sections: string[] = [];

  if (locations.length) {
    const items = locations
      .map(
        (loc) =>
          `<div class="trade-popup__item"><span class="trade-popup__item-dot" aria-hidden="true"></span><span>${escapeTradePopupHtml(loc)}</span></div>`
      )
      .join('');
    sections.push(
      `<section class="trade-popup__section">
          <div class="trade-popup__list">${items}</div>
        </section>`
    );
  }

  if (includeMail && mailLabel) {
    sections.push(
      `<section class="trade-popup__section">
          <p class="trade-popup__mail">${escapeTradePopupHtml(mailLabel)}</p>
        </section>`
    );
  }

  return `<div class="trade-popup">${sections.join('')}</div>`;
}

/** API may return p_return_message as a JSON string or an already-parsed object. */
export function parseTradeProcedureReturnMessage(raw: unknown): any | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw === 'object') {
    return raw;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return null;
}

export function isEmptyTradeProcedureReturnMessage(raw: unknown): boolean {
  if (raw == null || raw === '') {
    return true;
  }
  if (typeof raw === 'string') {
    return !raw.trim();
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.keys(raw as object).length === 0;
  }
  return false;
}

/** One row per item_id; qty is duplicate count in the same list. */
export type TradeDisplayEntry = { item: any; qty: number; repeated: boolean };

export function isTradeRepeatedItem(item: any): boolean {
  const type = String(item?.type ?? '')
    .trim()
    .toLowerCase();
  return type === 'receive full' || type === 'give full';
}

export function getTradeDisplayEntries(items: any[]): TradeDisplayEntry[] {
  if (!Array.isArray(items) || !items.length) {
    return [];
  }

  const order: string[] = [];
  const counts = new Map<string, number>();
  const first = new Map<string, any>();
  const repeated = new Map<string, boolean>();

  for (const item of items) {
    if (item?.item_id == null) {
      continue;
    }
    const id = String(item.item_id);
    if (!first.has(id)) {
      first.set(id, item);
      order.push(id);
    } else if (
      isTradeRepeatedItem(item) &&
      !isTradeRepeatedItem(first.get(id))
    ) {
      first.set(id, item);
    }
    if (isTradeRepeatedItem(item)) {
      repeated.set(id, true);
    }
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  return order.map((id) => ({
    item: first.get(id),
    qty: counts.get(id) ?? 1,
    repeated: repeated.get(id) ?? isTradeRepeatedItem(first.get(id)),
  }));
}

/** @deprecated Prefer getTradeDisplayEntries — counts duplicates per row. */
export function getTradeItemDisplayQty(items: any[], item: any): number {
  if (!item?.item_id || !Array.isArray(items)) {
    return 0;
  }
  const id = String(item.item_id);
  const count = items.filter((i) => String(i?.item_id) === id).length;
  return count > 1 ? count : 0;
}
